import { DIFFICULTY, type DifficultyStep } from '@/config/difficulty';
import { GAME } from '@/config/game';
import { MAPS, type ChamberSize } from '@/config/maps';
import {
  ENDLESS_THEME_CYCLE, ENDLESS_THEME_ROOMS, THEMES, type ThemeDef,
} from '@/config/themes';
import { WAVE_LAYOUT } from '@/config/waves';
import { LAYOUTS } from '@/arena/layouts';
import type { MinionKind } from '@/entities/minion-defs';
import type { RunState } from './run-state';

/**
 * THE difficulty hook point (EPIC D2): boss HP, damage taken, wave density,
 * elite share and kill payouts all read their multiplier here. Endless runs
 * get the identity (`DIFFICULTY.base`); sector runs their sector's row.
 */
export function difficultyFor(run: RunState): DifficultyStep {
  if (run.mode !== 'sector') return DIFFICULTY.base;
  return DIFFICULTY.sectors[run.sectorIndex] ?? DIFFICULTY.base;
}

/**
 * Victory check on the boss-death → portal path (EPIC D3).
 * @returns true when the finale of a sector run just fell — the victory
 *   flow takes over there instead of the portal.
 */
export function isSectorVictory(run: RunState, bossId: string): boolean {
  return run.inFinale && run.currentFight?.id === bossId;
}

/** Extra finale HP (finale modifier); 1 outside the finale room. */
export function finaleHpMul(run: RunState): number {
  return run.inFinale ? run.sector!.finaleMod.hpMul : 1;
}

/** LAYOUTS index of a room: the sector's layout list, or the endless cycle. */
export function layoutIndexFor(run: RunState, room: number, endlessIndex: number): number {
  const sector = run.sector;
  if (!sector) return endlessArena(endlessIndex);
  const id = sector.layouts[Math.min(room, sector.layouts.length - 1)]!;
  const at = LAYOUTS.findIndex((l) => l.id === id);
  return at >= 0 ? at : endlessArena(endlessIndex);
}

/** Endless keeps its classic flow: arena layouts only (routes are skipped). */
function endlessArena(index: number): number {
  for (let k = 0; k < LAYOUTS.length; k++) {
    const at = (index + k) % LAYOUTS.length;
    if (!LAYOUTS[at]!.route) return at;
  }
  return 0;
}

/** Map theme of a room: the sector's, or endless's stretch of 7 rooms. */
export function themeFor(run: RunState, room: number): ThemeDef {
  const sector = run.sector;
  if (sector) return THEMES[sector.theme];
  const cycle = ENDLESS_THEME_CYCLE;
  return THEMES[cycle[Math.floor(room / ENDLESS_THEME_ROOMS) % cycle.length]!];
}

/** Boss chamber of an advance room — large for bosses that roam wide. */
export function chamberFor(bossId: string | undefined): ChamberSize {
  const C = MAPS.chamber;
  return bossId && (C.largeBosses as ReadonlyArray<string>).includes(bossId) ? C.large : C.normal;
}

/** Rooms of the run's sector incl. the finale (0 in endless). */
export function sectorRoomCount(run: RunState): number {
  const sector = run.sector;
  return sector ? sector.rooms + 1 : 0;
}

/** Everything the wave plan needs to know about a room (docs/01 D5). */
export interface WaveRules {
  /** Sector rooms: [lo, hi] waves; null = the endless depth ceiling */
  readonly waves: readonly [number, number] | null;
  /** Light kinds introduced by this room */
  readonly kinds: ReadonlyArray<MinionKind>;
  readonly eliteRatio: number;
  /** Room gates (room-in-sector) for bunkers / the dome / side squads */
  readonly bunkerFrom: number;
  readonly domeFrom: number;
  readonly addsFrom: number;
}

/** Wave rules of `room` in this run (the sector plan, or the endless gates). */
export function waveRulesFor(run: RunState, room: number): WaveRules {
  const sector = run.sector;
  const eliteRatio = difficultyFor(run).eliteRatio;
  if (!sector) {
    const w = GAME.run.waves;
    return {
      waves: null, kinds: WAVE_LAYOUT.squadKinds, eliteRatio,
      bunkerFrom: w.bunkerFromRoom, domeFrom: w.domeFromRoom, addsFrom: GAME.run.bossAdds.minRoom,
    };
  }
  const plan = sector.plan[Math.min(room, sector.plan.length - 1)]!;
  return {
    waves: plan.kinds.length > 0 ? plan.waves : [0, 0],
    kinds: plan.kinds,
    eliteRatio,
    bunkerFrom: sector.gates.bunker,
    domeFrom: sector.gates.dome,
    addsFrom: sector.gates.adds,
  };
}
